# The role-key algebra, in the ONE place both sides of the wire may read it.
#
# WHY THIS MODULE EXISTS. principal.coffee owns this rule for the SERVER, and
# the comment at model.coffee:820 records why: the hand-built "#{type}.#{role}"
# sites were routed through findAclForPrincipal so the union rule lives once
# and not six times. The admin client is the set of sites that never were —
# Drawer, AppMain.hasPermission, Header and authAdmin each built ONE key from a
# single remembered role, so a Sales+Agent user was shown none of the models
# the server would serve her (card 6a85ce2a76eff36e3533f6fc). Putting the rule
# in shared/ is what stops the client's copy drifting from the server's the
# next time either changes: principal.coffee delegates here, client_api.coffee
# imports the same functions, and there is no second implementation to forget.
#
# Shape-agnostic on purpose. Everything here takes a plain "role holder" —
# anything carrying `role` (the historical scalar), `roles` (the array) and
# optionally `scopedRole`. A server principal's cache_obj is one; so is a user
# row; so is the client's own session state. Nothing here knows about auth
# tokens, redis or localStorage.
#
# CommonJS, like credentialOperations.coffee and stepUpOperations.coffee: the
# server `require`s it and the client bundle imports it as a named import.

# THE ROLES A HOLDER HOLDS, normalized from either storage shape. A row may
# carry `role: 'Agent'` (string — the historical shape) OR
# `roles: ['Agent','Sales']` (array — written once a second role is added via
# the UI). The roles array wins when it is a non-empty array; otherwise the
# scalar becomes a one-element set. There is NO migration: both shapes are
# first-class forever (owner decision 2026-08-15, card
# 6a76228524c38a914d75ac8d).
#
# Blank entries are dropped and duplicates collapsed, so a hand-edited
# roles: ['Agent','Agent',''] cannot double-grant or manufacture an
# empty-string role. Returns [] for a roleless holder — callers that need the
# wildcard degradation use roleKeysFor below, which owns that rule.
heldRoles = (obj) ->
  return [] unless obj?
  raw =
    if Array.isArray(obj.roles) and obj.roles.length > 0 then obj.roles
    else if obj.role? then [obj.role]
    else []
  out = []
  for r in raw when r? and "#{r}".length > 0
    out.push r unless r in out
  out

# The roles a holder RESOLVES at right now. Ordinarily the full held set (the
# union — owner decision 2026-08-15: a person who is both a teacher and a
# district administrator is both, all the time). When the session carries a
# transient scopedRole (Card 2's stand-down control), resolution uses ONLY that
# role: scoping re-imposes every restriction of the chosen role. scopedRole is
# NOT required to be inside the held set — an Admin may scope to any role — so
# it is taken verbatim, never intersected.
effectiveRoles = (obj) ->
  return [obj.scopedRole] if obj?.scopedRole?
  heldRoles(obj)

# The PRIMARY/DISPLAY role — the scope when scoped, else the first held role.
# This is what audit rows and display sites want (spec item 4: the union
# machinery must not widen what gets logged). ACL RESOLUTION must NOT use it.
primaryRole = (obj) ->
  obj?.scopedRole ? (heldRoles(obj)[0] ? obj?.role)

# EVERY ACL lookup key this holder resolves at — one "#{type}.#{role}" per
# effective role.
#
# THE ZERO CASE IS LOAD-BEARING. The union of ZERO roles degrades to
# ["#{type}.undefined"], i.e. to the wildcard ACL, NEVER to unrestricted and
# never to the empty list. findAclForRole falls back from an exact miss to the
# "#{type}.*" wildcard, and that wildcard is what carries restrictToOwnership
# for a principal whose role failed to resolve. An empty list would make every
# "does any key match" test answer false on the server and — on the client —
# would read as "this caller resolves at nothing", which the drawer would then
# have to special-case. "No roles" and "all roles" are one typo apart, so the
# degradation lives here and nowhere else.
roleKeysFor = (type, obj) ->
  roles = effectiveRoles(obj)
  return ["#{type}.#{primaryRole(obj)}"] if roles.length == 0
  "#{type}.#{r}" for r in roles

# Does this holder resolve at `key` ("User.Admin" and friends)? The multi-role
# replacement for `roleKey(p) == 'User.Admin'` equality checks: a user holding
# [Agent, Admin] must pass an Admin gate even though their primary/display role
# is Agent. Scoping narrows this too — an Admin scoped down to Agent does NOT
# hold User.Admin for the scoped session, which is the entire point of
# stand-down.
holdsRoleKeyFor = (type, obj, key) ->
  key in roleKeysFor(type, obj)

# Resolve `key` against a model's `acls` map the way the SERVER does: try each
# key the holder resolves at, then that key's "#{type}.*" wildcard, and hand
# every ACL that matched to the caller. Returns [] when nothing matched, which
# is the client's "this caller has no role-specific access" answer.
#
# UNION, NOT FIRST-MATCH. documentAcl.coffee:248 states the rule for per-row
# grants — "a document grant to ANY held role admits the caller (union, owner
# decision 2026-08-15)" — and model.coffee findAclForPrincipal does the same
# for model ACLs. A client that stopped at the first matching key would answer
# for whichever role happened to be listed first, which is the single-role bug
# wearing a loop.
aclsForRoleKeys = (acls, keys) ->
  return [] unless acls? and not Array.isArray(acls)
  out = []
  seen = {}
  for key in keys
    for candidate in [key, wildcardFor(key)] when candidate?
      continue if seen[candidate]
      seen[candidate] = true
      out.push acls[candidate] if acls[candidate]?
  out

# "User.Agent" -> "User.*". Null for a key with no type separator, so a
# malformed key cannot manufacture a "*" that matches everything.
wildcardFor = (key) ->
  dot = "#{key}".indexOf('.')
  return null if dot <= 0
  "#{"#{key}".substring(0, dot)}.*"

module.exports = {
  heldRoles
  effectiveRoles
  primaryRole
  roleKeysFor
  holdsRoleKeyFor
  aclsForRoleKeys
  wildcardFor
}
